import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import { Home } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../services/api';
import { API } from '../../App';
import 'leaflet/dist/leaflet.css';

const pinIcon = L.divIcon({
  html: renderToStaticMarkup(
    <div style={{ background: '#ee7203', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
      <Home size={17} color="white" strokeWidth={2.5} />
    </div>
  ),
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

/** Recenters the map once, whenever the loaded position first becomes available
 * (postcode geocode resolves, or a saved override loads) — never again after that,
 * so dragging the pin around doesn't fight the map's own view. */
const RecenterOnce = ({ center }) => {
  const map = useMap();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (center && !done) {
      map.setView([center.lat, center.lng], 15);
      setDone(true);
    }
  }, [center, done, map]);
  return null;
};

/**
 * Settings > Vehicles > Base Location — lets a super_admin fine-tune the EXACT
 * depot/yard position by dragging a pin on a real map, instead of relying purely
 * on the base postcode's geocoded centroid (which can be meaningfully off from
 * where vehicles actually park — see TransportMapPanel's live tracking overlay).
 * `value` is `{lat,lng} | null` (null = "use the postcode, no override yet");
 * `onChange` is called with the new `{lat,lng}` on drag, or `null` on Reset.
 */
export const BaseLocationMapPicker = ({ basePostcode, value, onChange }) => {
  const [postcodeCenter, setPostcodeCenter] = useState(null);

  useEffect(() => {
    if (!basePostcode) return;
    let cancelled = false;
    api.get(`${API}/postcode/${encodeURIComponent(basePostcode.trim().toUpperCase().replace(/\s+/g, ''))}`)
      .then((res) => { if (!cancelled) setPostcodeCenter(res.data); })
      .catch(() => { if (!cancelled) setPostcodeCenter(null); });
    return () => { cancelled = true; };
  }, [basePostcode]);

  const center = value || postcodeCenter;
  if (!center) {
    return <div className="text-xs text-gray-400 py-4" data-testid="base-location-picker-loading">Loading map…</div>;
  }

  return (
    <div className="space-y-2" data-testid="base-location-picker">
      <div className="rounded-lg overflow-hidden border" style={{ height: 260 }}>
        <MapContainer center={[center.lat, center.lng]} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <RecenterOnce center={center} />
          <Marker
            position={[center.lat, center.lng]}
            icon={pinIcon}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const { lat, lng } = e.target.getLatLng();
                onChange({ lat, lng });
              },
            }}
            data-testid="base-location-draggable-pin"
          />
        </MapContainer>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          {value ? `Manual pin set — ${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}` : 'Drag the pin onto the exact depot entrance/yard — currently using the postcode\'s geocoded centre.'}
        </p>
        {value && (
          <Button type="button" variant="outline" size="sm" onClick={() => onChange(null)} data-testid="base-location-reset-btn">
            Reset to postcode
          </Button>
        )}
      </div>
    </div>
  );
};
